import React from 'react';
import { Type, Barcode, QrCode, Image as ImageIcon, Square, Minus, Table as TableIcon, LucideIcon } from 'lucide-react';
import { LabelElementType } from '../../../../../types/labelDesign';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ElementToolboxProps {
  onAdd: (type: LabelElementType) => void;
  /**
   * Rapporttype — Table-verktøyet vises kun for dokumenttyper (10–14):
   * pakkseddel, plukkliste, telleliste, innlagringsplan.
   */
  reportType?: number;
  /** Layouten har allerede et Table-element (maks ett per layout). */
  hasTable?: boolean;
}

const TOOLS: Array<{ type: LabelElementType; label: string; Icon: LucideIcon }> = [
  { type: 'Text', label: 'Tekst', Icon: Type },
  { type: 'Barcode', label: 'Strekkode', Icon: Barcode },
  { type: 'QrCode', label: 'QR-kode', Icon: QrCode },
  { type: 'Image', label: 'Bilde/logo', Icon: ImageIcon },
  { type: 'Rectangle', label: 'Rektangel', Icon: Square },
  { type: 'Line', label: 'Linje', Icon: Minus }
];

const isDocumentType = (reportType?: number) =>
  reportType != null && reportType >= 10 && reportType <= 14;

export const ElementToolbox: React.FC<ElementToolboxProps> = ({ onAdd, reportType, hasTable }) => {
  const { t } = useTranslation();

  const toolLabels: Record<LabelElementType, string> = {
    Text: t('manager.modules.labelDesigner.toolbox.text', {}, 'Tekst'),
    Barcode: t('manager.modules.labelDesigner.toolbox.barcode', {}, 'Strekkode'),
    QrCode: t('manager.modules.labelDesigner.toolbox.qrCode', {}, 'QR-kode'),
    Image: t('manager.modules.labelDesigner.toolbox.image', {}, 'Bilde/logo'),
    Rectangle: t('manager.modules.labelDesigner.toolbox.rectangle', {}, 'Rektangel'),
    Line: t('manager.modules.labelDesigner.toolbox.line', {}, 'Linje'),
    Table: t('manager.modules.labelDesigner.toolbox.table', {}, 'Tabell')
  };

  const tools: Array<{ type: LabelElementType; Icon: LucideIcon; disabled?: boolean; disabledHint?: string }> = [
    ...TOOLS,
    // Linjetabell — kun dokumenttyper, maks ett Table-element per layout
    ...(isDocumentType(reportType)
      ? [{
          type: 'Table' as LabelElementType,
          Icon: TableIcon,
          disabled: hasTable,
          disabledHint: t('manager.modules.labelDesigner.toolbox.tableExists', {}, 'Layouten har allerede en tabell (maks én)')
        }]
      : [])
  ];

  return (
    <div className="flex flex-col gap-1 p-2 bg-[var(--color-surface)] border-r border-[var(--color-border)]">
      <div className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase mb-1 px-1">
        {t('manager.modules.labelDesigner.toolbox.heading', {}, 'Elementer')}
      </div>
      {tools.map(({ type, Icon, disabled, disabledHint }) => {
        const label = toolLabels[type];
        return (
          <button
            key={type}
            type="button"
            onClick={() => onAdd(type)}
            disabled={disabled}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-primary)] text-left w-40 disabled:opacity-40 disabled:cursor-not-allowed"
            title={disabled
              ? disabledHint
              : t('manager.modules.labelDesigner.toolbox.addTooltip', { '0': label.toLowerCase() }, `Legg til ${label.toLowerCase()}`)}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
};
